import type { DateRange } from "@repo/app-types/entities/dateRange";
import {
  assertUnreachable,
  isNotNullNotUndefined,
} from "@repo/app-types/helpers/utilities";
import { AnchoredTooltip } from "@repo/ui/tooltip/anchoredTooltip";
import {
  type FC,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";

/** Data for a line in the chart */
export interface LineData {
  /** Unique identifier for this line */
  key: string;
  /** Display label for the legend. Only needed for multi-line charts with legend. */
  label?: string;
  /** CSS color value (hex, rgb, etc.). If omitted for single-line, uses getColorClassName */
  color?: string;
  /** Data points for this line */
  data: readonly (number | null | undefined)[];
}

export interface TrendChartProps {
  className?: string;
  minYRange: number;
  /**
   * Line data for the chart. Can be a single line or multiple lines.
   * For single-line charts without explicit color, the getColorClassName prop is used.
   */
  lines: readonly LineData[];
  renderValue: (value: number) => string;
  showsXAxisLabels?: boolean;
  showsYAxisLabels?: boolean;
  /** Whether to show the legend for multi-line charts */
  showsLegend?: boolean;
  /** Function to get the color class name based on the deltaY value (single line mode without explicit color) */
  getColorClassName?: (deltaY: number) => string;
  /** Custom x-axis labels. Falls back to "{N}d ago" / "today" when omitted. */
  xAxisLabels?: { start: string; end: string };
  /**
   * Custom tooltip date label per data point. When provided, overrides the
   * default "{N}d ago" / "today" labels — use this when the chart's right edge
   * isn't anchored at "now" (e.g. a user-selected date range ending in the past).
   */
  getTooltipDateLabel?: (index: number) => string;
  /** Time unit for tooltip and x-axis labels. Defaults to "day". */
  timeUnit?: "day" | "week" | "month";
}

const timeUnitShortMap = {
  day: "d",
  week: "w",
  month: "mo",
} as const;

type TimeUnit = keyof typeof timeUnitShortMap;

function timeUnitShort(unit: TimeUnit = "day") {
  return timeUnitShortMap[unit];
}

// TODO: Actually respect the date range — the last data point is not "today"
// when the queried range ends in the past. Used by both the default x-axis end
// label and the default tooltip date label.
function nowForUnit(unit: TimeUnit = "day") {
  switch (unit) {
    case "day":
      return "Today";
    case "week":
      return "This week";
    case "month":
      return "This month";
    default:
      assertUnreachable(unit);
  }
}

const horizontalPadding = 3;
const verticalPadding = 5;

export const TrendChart: FC<TrendChartProps> = (props) => {
  const { lines } = props;

  const isSingleLineWithoutExplicitColor =
    lines.length === 1 && !lines[0]?.color;

  // Compute global bounds from all lines
  const { minX, maxX, minY, maxY, visualMinY, visualMaxY } = useMemo(() => {
    const maxLength = Math.max(1, ...lines.map((line) => line.data.length));
    const minX = 0;
    const maxX = Math.max(maxLength - 1, 0);

    // Collect all Y values from all lines
    const allYs: number[] = [];
    for (const line of lines) {
      for (const value of line.data) {
        if (isNotNullNotUndefined(value)) {
          allYs.push(value);
        }
      }
    }

    const minY = allYs.length ? Math.min(...allYs) : 0;
    const maxY = allYs.length ? Math.max(...allYs) : 1;
    const midY = (minY + maxY) / 2;

    // Extend the Y range if it's too small, especially when we have a flat line
    let visualMinY = Math.min(minY, midY - props.minYRange / 2);
    const visualMaxY = Math.max(maxY, midY + props.minYRange / 2);

    // Never render below zero — data is non-negative and cubic interpolation
    // between consecutive zeros can dip the visual min negative.
    visualMinY = Math.max(0, visualMinY);

    return { minX, maxX, minY, maxY, visualMinY, visualMaxY };
  }, [lines, props.minYRange]);

  const [width, setWidth] = useState(240);
  const [height, setHeight] = useState(100);

  const convertDataPointToCoordinates = useMemo(
    () =>
      (point: readonly [number, number]): readonly [number, number] => {
        // Guard against a single-point series (maxX === minX), which would
        // otherwise divide by zero and produce NaN coordinates.
        const x =
          ((point[0] - minX) / Math.max(maxX - minX, 1)) *
            (width - horizontalPadding * 2) +
          horizontalPadding;
        const y =
          (1 - (point[1] - visualMinY) / (visualMaxY - visualMinY)) *
            (height - verticalPadding * 2) +
          verticalPadding;
        return [x, y] as const;
      },
    [minX, maxX, visualMinY, visualMaxY, width, height],
  );

  const baselineY = height - verticalPadding;

  // Compute processed line data (coords, paths) for all lines
  const processedLines = useMemo(() => {
    return lines.map((line) => {
      const dataPoints = extractDataPoints(line.data, minX);
      const coords = dataPoints.map(convertDataPointToCoordinates);
      const pathD = toCubicPathD(coords, 0.6, baselineY);
      const areaD = makeAreaPath(pathD, coords, baselineY);

      return {
        key: line.key,
        label: line.label,
        color: line.color,
        coords,
        pathD,
        areaD,
        lastCoords: coords.at(-1),
        // For single-line deltaY calculation
        firstY: dataPoints.at(0)?.[1],
        lastY: dataPoints.at(-1)?.[1],
      };
    });
  }, [lines, minX, convertDataPointToCoordinates, baselineY]);

  // Calculate deltaY for single-line color class (based on first line if exists)
  const deltaY = useMemo(() => {
    const firstLine = processedLines[0];
    if (!firstLine) return 0;

    const startY = firstLine.firstY ?? 0;
    const endY = firstLine.lastY ?? startY;
    return endY - startY;
  }, [processedLines]);

  const svgContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const svgContainer = svgContainerRef.current;
    if (!svgContainer) {
      return;
    }

    const window = svgContainer.ownerDocument.defaultView;
    if (!window) {
      return;
    }

    const updateSize = () => {
      setWidth(svgContainer.clientWidth);
      setHeight(svgContainer.clientHeight);
    };

    updateSize();

    const observer = new window.ResizeObserver((entries) => {
      const entry = entries.at(-1);
      if (!entry) {
        return;
      }

      updateSize();
    });
    observer.observe(svgContainer);

    return () => {
      observer.disconnect();
    };
  }, []);

  const gradientId = useId();

  // Hover state
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const chartWidth = width - horizontalPadding * 2;
      const relativeX = mouseX - horizontalPadding;
      const index = Math.round((relativeX / chartWidth) * maxX);
      setHoveredIndex(Math.max(0, Math.min(maxX, index)));
    },
    [width, maxX],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  const hoveredX = useMemo(() => {
    if (hoveredIndex === null) return null;
    const [x] = convertDataPointToCoordinates([hoveredIndex, 0]);
    return x;
  }, [hoveredIndex, convertDataPointToCoordinates]);

  const hoveredValues = useMemo(() => {
    if (hoveredIndex === null) return null;
    return lines.map((line) => {
      const value = line.data[hoveredIndex];
      return {
        key: line.key,
        color: line.color,
        value: isNotNullNotUndefined(value) ? value : null,
      };
    });
  }, [hoveredIndex, lines]);
  const tooltipAnchorRef = useRef<HTMLSpanElement>(null);

  // Color class for single-line mode without explicit color
  const colorClassName = props.getColorClassName
    ? props.getColorClassName(deltaY)
    : [
        deltaY > 0 ? "text-success" : "",
        deltaY < 0 ? "text-destructive" : "",
        deltaY === 0 ? "text-muted-foreground" : "",
      ].join(" ");

  const showsLegend =
    props.showsLegend && lines.length > 1 && !isSingleLineWithoutExplicitColor;

  return (
    <div
      className={twMerge(
        "grid grid-cols-[auto_1fr] grid-rows-[1fr_auto]",
        showsLegend ? "gap-y-2" : "",
        props.className,
      )}
    >
      {props.showsYAxisLabels ? (
        <div className="mr-0.5 flex flex-col justify-between text-[0.625rem] leading-none font-medium text-muted-foreground">
          <div>{props.renderValue(maxY)}</div>
          <div>{props.renderValue(minY)}</div>
        </div>
      ) : null}

      <div
        ref={svgContainerRef}
        className="relative col-start-2 overflow-hidden"
      >
        <svg
          className={twMerge(
            "absolute size-full",
            isSingleLineWithoutExplicitColor ? colorClassName : "",
          )}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <defs>
            {isSingleLineWithoutExplicitColor ? (
              // Single gradient using currentColor for single-line without explicit color
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            ) : (
              // Per-line gradients for multi-line or explicit colors
              processedLines.map((line) => (
                <linearGradient
                  key={`gradient-${line.key}`}
                  id={`${gradientId}-${line.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={line.color} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={line.color} stopOpacity="0" />
                </linearGradient>
              ))
            )}
          </defs>

          {processedLines.map((line) => {
            const useCurrentColor = isSingleLineWithoutExplicitColor;
            const fillUrl = useCurrentColor
              ? `url(#${gradientId})`
              : `url(#${gradientId}-${line.key})`;
            const strokeColor = useCurrentColor ? "currentColor" : line.color;
            const strokeWidth = 1;
            const circleRadius = 2;

            return (
              <g key={line.key}>
                {line.areaD ? (
                  <path d={line.areaD} fill={fillUrl} stroke="none" />
                ) : null}
                {line.pathD && line.coords.length >= 2 ? (
                  <path
                    d={line.pathD}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                  />
                ) : null}
                {line.lastCoords ? (
                  <circle
                    cx={line.lastCoords[0]}
                    cy={line.lastCoords[1]}
                    r={circleRadius}
                    fill={strokeColor}
                  />
                ) : null}
              </g>
            );
          })}

          {/* Hover elements */}
          {hoveredX !== null && hoveredValues && (
            <>
              <line
                x1={hoveredX}
                y1={verticalPadding}
                x2={hoveredX}
                y2={height - verticalPadding}
                stroke="#9CA3AF"
                strokeWidth={1}
                strokeDasharray="3,3"
              />
              {hoveredValues.map((hv) => {
                if (hv.value === null) return null;
                const [, y] = convertDataPointToCoordinates([
                  hoveredIndex!,
                  hv.value,
                ]);
                const dotColor = isSingleLineWithoutExplicitColor
                  ? "currentColor"
                  : hv.color;
                return (
                  <circle
                    key={hv.key}
                    cx={hoveredX}
                    cy={y}
                    r={4}
                    fill={dotColor}
                    stroke="white"
                    strokeWidth={2}
                  />
                );
              })}
            </>
          )}

          {/* Invisible overlay for mouse events */}
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "crosshair" }}
          />
        </svg>

        {/* Anchor the shared tooltip to the hovered chart position. */}
        {hoveredX !== null ? (
          <span
            ref={tooltipAnchorRef}
            aria-hidden
            className="pointer-events-none absolute top-0 size-px"
            style={{ left: hoveredX }}
          />
        ) : null}
        {hoveredValues && hoveredIndex !== null ? (
          <AnchoredTooltip triggerRef={tooltipAnchorRef}>
            <div className="mb-0.5 font-medium text-gray-300">
              {props.getTooltipDateLabel
                ? props.getTooltipDateLabel(hoveredIndex)
                : hoveredIndex === maxX
                  ? (props.xAxisLabels?.end ?? nowForUnit(props.timeUnit))
                  : `${maxX - hoveredIndex}${timeUnitShort(props.timeUnit)} ago`}
            </div>
            {hoveredValues.map((hv) => {
              if (hv.value === null) return null;
              return (
                <div key={hv.key} className="flex items-center gap-1">
                  {lines.length > 1 && (
                    <span
                      className="inline-block size-2 rounded-full"
                      style={{ backgroundColor: hv.color }}
                    />
                  )}
                  <span>{props.renderValue(hv.value)}</span>
                </div>
              );
            })}
          </AnchoredTooltip>
        ) : null}
      </div>

      {props.showsXAxisLabels ? (
        <div className="col-start-2 flex justify-between text-[0.625rem] leading-none font-medium text-muted-foreground">
          {/* The last point is "today" (index maxX), so the first point sits maxX time units back. */}
          <div>
            {props.xAxisLabels?.start ??
              `${maxX}${timeUnitShort(props.timeUnit)} ago`}
          </div>
          <div>{props.xAxisLabels?.end ?? nowForUnit(props.timeUnit)}</div>
        </div>
      ) : null}

      {showsLegend ? (
        <div className="col-span-2 flex flex-wrap gap-x-3 gap-y-1">
          {lines.map((line) => (
            <div
              key={line.key}
              className="flex items-center gap-1 text-[0.625rem] font-medium"
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: line.color }}
              />
              <span className="text-gray-600">{line.label ?? line.key}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Extracts data points from raw data array, filtering out nulls and converting to [x, y] tuples.
 * Also handles left extrapolation if the first data point doesn't start at minX.
 */
function extractDataPoints(
  data: readonly (number | null | undefined)[],
  minX: number,
): (readonly [number, number])[] {
  const dataPoints = data
    .map((value, index) =>
      isNotNullNotUndefined(value) ? ([index, value] as const) : null,
    )
    .filter(isNotNullNotUndefined);

  // Extrapolate the left part of the chart if necessary
  const firstPoint = dataPoints.at(0);
  if (firstPoint && firstPoint[0] > minX) {
    dataPoints.unshift([minX, firstPoint[1]]);
  }

  return dataPoints;
}

/**
 * Generates an area path string for filling under a line.
 */
function makeAreaPath(
  linePathD: string,
  coords: readonly (readonly [number, number])[],
  baselineY: number,
): string {
  const firstCoords = coords.at(0);
  const lastCoords = coords.at(-1);

  if (!firstCoords || !lastCoords || !linePathD) {
    return "";
  }

  return `${linePathD} L ${lastCoords[0]},${baselineY} L ${firstCoords[0]},${baselineY} Z`;
}

function toCubicPathD(
  points: readonly (readonly [number, number])[],
  tension = 0.6,
  /** In SVG coordinates, the maximum Y value (bottom edge) control points may reach. */
  clampMaxY?: number,
): string {
  const firstPoint = points[0];
  if (firstPoint === undefined) {
    return "";
  }

  if (points.length === 1) {
    return `M ${firstPoint[0]},${firstPoint[1]}`;
  }

  let d = `M ${firstPoint[0]},${firstPoint[1]}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    if (p1 === undefined || p2 === undefined) {
      continue;
    }
    const p0 = points[i - 1] ?? p1;
    const p3 = points[i + 2] ?? p2;

    // For time-series data, X should always move forward (left to right).
    // We only apply the Catmull-Rom spline tension to Y values to avoid curves going backwards in time.
    // Control points are placed at 1/3 and 2/3 of the way between p1 and p2 on the X axis.
    const cp1x = p1[0] + (p2[0] - p1[0]) / 3;
    let cp1y = p1[1] + ((p2[1] - p0[1]) / 6) * tension;
    const cp2x = p1[0] + ((p2[0] - p1[0]) * 2) / 3;
    let cp2y = p2[1] - ((p3[1] - p1[1]) / 6) * tension;

    // Clamp control points so the cubic curve never dips past the bottom edge
    // (e.g. below data-space zero when consecutive points sit at zero).
    if (clampMaxY !== undefined) {
      cp1y = Math.min(cp1y, clampMaxY);
      cp2y = Math.min(cp2y, clampMaxY);
    }

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
  }

  return d;
}

/**
 * Number of days covered by `dateRange`.
 */
export function countTrendDays(dateRange: DateRange): number {
  return Math.round(dateRange.length.as("days"));
}
